<template>
  <div>
    <b-row class="mb-2">
      <b-col class="text-right">
        <b-button
          variant="primary"
          size="sm"
          @click="openAddVehicle"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          Adicionar Veículo à Viagem
        </b-button>
      </b-col>
    </b-row>

    <!-- Lista de Veículos do Evento -->
    <b-table
      :items="veiculosEvento"
      :fields="fields"
      responsive
      primary-key="id"
      show-empty
      empty-text="Nenhum veículo alocado para este evento."
    >
      <template #cell(veiculo)="data">
        {{ data.value.descricao }} ({{ data.value.placa }})
      </template>
      <template #cell(acoes)="data">
        <b-button
          variant="info"
          size="sm"
          class="mr-50"
          @click="verMapa(data.item)"
        >
          <feather-icon
            icon="MapIcon"
            class="mr-25"
          />
          Assentos
        </b-button>
        <b-button
          variant="flat-danger"
          size="sm"
          @click="removerVeiculo(data.item.id)"
        >
          <feather-icon icon="TrashIcon" />
        </b-button>
      </template>
    </b-table>

    <!-- Modal Adicionar Veículo -->
    <b-modal
      id="modal-add-vehicle"
      title="Alocar Veículo"
      @ok="confirmAddVehicle"
    >
      <b-form-group label="Selecione o Veículo da Frota">
        <v-select
          v-model="newVehicle.veiculo"
          :options="frota"
          label="descricao"
        >
          <template #option="option">
            {{ option.descricao }} - {{ option.placa }} ({{ option.qtoLugares }} lug)
          </template>
        </v-select>
      </b-form-group>
      <b-form-group label="Motorista (Opcional)">
        <b-form-input
          v-model="newVehicle.motorista"
          placeholder="Nome do motorista..."
        />
      </b-form-group>
    </b-modal>

    <!-- Modal Mapa de Assentos -->
    <b-modal
      id="modal-mapa"
      title="Mapeamento de Assentos"
      size="lg"
      hide-footer
    >
      <div
        v-if="selectedVeiculo"
        class="text-center"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">
            {{ selectedVeiculo.veiculo.descricao }} - {{ selectedVeiculo.veiculo.placa }}
          </h5>
          <b-badge variant="light-info">
            {{ assentosOcupados.length }} / {{ selectedVeiculo.veiculo.qtoLugares }} Ocupados
          </b-badge>
        </div>

        <!-- Grade de Assentos -->
        <div
          class="seat-grid d-flex flex-wrap justify-content-center p-2 border rounded bg-light"
          style="max-height: 400px; overflow-y: auto;"
        >
          <div
            v-for="n in selectedVeiculo.veiculo.qtoLugares"
            :key="n"
            v-b-tooltip.hover="getSeatTooltip(n)"
            class="seat-box m-25 p-50 border rounded text-center cursor-pointer position-relative"
            :class="getSeatClass(n)"
            @click="manageSeat(n)"
          >
            <small class="d-block">#{{ n }}</small>
            <feather-icon
              :icon="isOccupied(n) ? 'UserCheckIcon' : 'UserIcon'"
              size="14"
            />
          </div>
        </div>

        <div class="mt-2 text-left">
          <small class="text-muted"><b-badge
            variant="success"
            class="mr-50"
          >&nbsp;</b-badge> Ocupado</small>
          <small class="text-muted ml-1"><b-badge
            variant="secondary"
            class="mr-50"
          >&nbsp;</b-badge> Livre</small>
        </div>
      </div>
    </b-modal>

    <!-- Modal Selecionar Participante -->
    <b-modal
      id="modal-select-participant"
      title="Atribuir Assento"
      @ok="confirmSeat"
    >
      <p>Selecione o participante para o assento <strong>#{{ currentSeatNumber }}</strong>:</p>
      <v-select
        v-model="selectedParticipante"
        :options="participantes"
        label="registro.nome"
      >
        <template #option="option">
          {{ option.registro.nome }} ({{ option.status }})
        </template>
      </v-select>
      <div
        v-if="isOccupied(currentSeatNumber)"
        class="mt-2"
      >
        <b-button
          variant="outline-danger"
          block
          size="sm"
          @click="liberarAssento"
        >
          Liberar este assento
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BTable, BModal, BButtonGroup, BFormGroup, BFormInput, BBadge, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  components: {
    BRow, BCol, BButton, BTable, BModal, BButtonGroup, BFormGroup, BFormInput, BBadge, vSelect,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: ['eventoId'],
  data() {
    return {
      veiculosEvento: [],
      frota: [],
      participantes: [],
      assentosOcupados: [],
      selectedVeiculo: null,
      currentSeatNumber: null,
      selectedParticipante: null,
      newVehicle: {
        veiculo: null,
        motorista: '',
      },
      fields: [
        { key: 'veiculo', label: 'Veículo' },
        { key: 'motorista', label: 'Motorista' },
        { key: 'veiculo.qtoLugares', label: 'Capacidade' },
        { key: 'acoes', label: 'Ações' },
      ],
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        const [evRes, frotaRes, partRes] = await Promise.all([
          this.$http.get(`/evento/evento-veiculos/evento/${this.eventoId}`),
          this.$http.get('/evento/veiculos'),
          this.$http.get(`/evento/evento_participantes/evento/${this.eventoId}`),
        ])
        this.veiculosEvento = evRes.data
        this.frota = frotaRes.data
        this.participantes = partRes.data
      } catch (e) {
        this.$toast.error('Erro ao carregar dados de logística')
      }
    },
    async verMapa(item) {
      this.selectedVeiculo = item
      try {
        const { data } = await this.$http.get(`/evento/veiculo-assentos/evento-veiculo/${item.id}`)
        this.assentosOcupados = data
        this.$bvModal.show('modal-mapa')
      } catch (e) {
        this.$toast.error('Erro ao carregar assentos')
      }
    },
    getSeatClass(n) {
      return this.isOccupied(n) ? 'bg-success text-white' : 'bg-white text-primary'
    },
    getSeatTooltip(n) {
      const seat = this.assentosOcupados.find(a => a.numeroAssento === n)
      return seat ? seat.participante.registro.nome : 'Disponível'
    },
    isOccupied(n) {
      return this.assentosOcupados.some(a => a.numeroAssento === n)
    },
    manageSeat(n) {
      this.currentSeatNumber = n
      const seat = this.assentosOcupados.find(a => a.numeroAssento === n)
      this.selectedParticipante = seat ? seat.participante : null
      this.$bvModal.show('modal-select-participant')
    },
    async confirmSeat() {
      if (!this.selectedParticipante) return

      const payload = {
        eventoVeiculo: { id: this.selectedVeiculo.id },
        participante: { id: this.selectedParticipante.id },
        numeroAssento: this.currentSeatNumber,
        andar: 1,
      }

      try {
        await this.$http.post('/evento/veiculo-assentos', payload)
        this.$toast.success('Assento atribuído')
        this.verMapa(this.selectedVeiculo)
      } catch (e) {
        this.$toast.error('Erro ao salvar assento')
      }
    },
    async liberarAssento() {
      const seat = this.assentosOcupados.find(a => a.numeroAssento === this.currentSeatNumber)
      if (seat) {
        try {
          await this.$http.delete(`/evento/veiculo-assentos/${seat.id}`)
          this.$toast.success('Assento liberado')
          this.$bvModal.hide('modal-select-participant')
          this.verMapa(this.selectedVeiculo)
        } catch (e) {
          this.$toast.error('Erro ao liberar assento')
        }
      }
    },
    openAddVehicle() {
      this.$bvModal.show('modal-add-vehicle')
    },
    async confirmAddVehicle() {
      if (!this.newVehicle.veiculo) return

      const payload = {
        evento: { id: this.eventoId },
        veiculo: { id: this.newVehicle.veiculo.id },
        motorista: this.newVehicle.motorista,
      }

      try {
        await this.$http.post('/evento/evento-veiculos', payload)
        this.$toast.success('Veículo adicionado')
        this.fetchData()
      } catch (e) {
        this.$toast.error('Erro ao adicionar veículo')
      }
    },
    async removerVeiculo(id) {
      try {
        await this.$http.delete(`/evento/evento-veiculos/${id}`)
        this.$toast.success('Veículo removido')
        this.fetchData()
      } catch (e) {
        this.$toast.error('Erro ao remover veículo')
      }
    },
  },
}
</script>

<style scoped>
.seat-box {
  width: 60px;
  height: 60px;
  transition: all 0.2s;
}
.seat-box:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  z-index: 10;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
